import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';

import { API_CheckTopUpGrant, BASEURL_POLYMER } from '../../utils/api';
import config from '../../config';
import { TopupGrantCaseList } from '../../models/polymer/TopupGrant.model';

const isProd =
  process.env.NODE_ENV === 'production' &&
  document.location.origin !== 'https://services3-dev.hdb.gov.sg' &&
  document.location.origin !== 'https://services3-stg.hdb.gov.sg';

interface QueryParams {
  hdb_ref: string;
  singpass_token: string;
  uinfin: string;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASEURL_POLYMER,
  prepareHeaders: (headers) => {
    headers.set('X-Content-Type-Options', 'nosniff');
    return headers;
  },
});

// ✅ note the 4th generic is now `{}` (NOT `unknown`)
const baseQueryWithSelectiveRetry: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  {}, // <-- important
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error) {
    const status = result.error.status;

    const shouldRetry = typeof status === 'number' && (status === 408 || status === 429 || status >= 500);

    // Don't retry non-transient errors (400/401/403/404...)
    if (!shouldRetry) {
      return retry.fail(result.error);
    }
  }

  return result;
};

const baseQuery = retry(baseQueryWithSelectiveRetry, { maxRetries: 1 });

export const topUpGrantApi = createApi({
  reducerPath: 'topUpGrantApi',
  baseQuery,
  endpoints: (builder) => ({
    getTopUpGrantStatus: builder.query<TopupGrantCaseList, QueryParams>({
      query: ({ hdb_ref, singpass_token, uinfin }) => ({
        method: 'GET',
        url: `${API_CheckTopUpGrant}`,
        params: { hdb_ref },
        headers: {
          Authorization: `Bearer ${singpass_token}`,
          ...(isProd ? {} : { uinfin }),
        },
      }),
      keepUnusedDataFor: 1,
    }),
  }),
});

export const { useGetTopUpGrantStatusQuery } = topUpGrantApi;
